// useTreeDraft.js
// react
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * UI-specific properties that should be excluded from tree comparisons and data persistence.
 * These properties are used for rendering state and tree navigation but are not part of the core data model.
 */
const TRANSIENT_KEYS = new Set([
  '_expanded',
  '_highlighted',
  '_upToTheRootHighlighted',
  '_children',
  'children',
  '_directSubordinates',
  '_totalSubordinates',
]);

/**
 * Removes transient UI properties from a tree node to get only the core data.
 * This is used when comparing nodes or preparing data for server operations.
 *
 * @param {Object|null} node - The tree node to clean
 * @returns {Object} A new object with only non-transient properties
 */
const stripTransient = (node) => {
  if (!node || typeof node !== 'object') return node;
  const out = {};
  for (const [k, v] of Object.entries(node)) {
    if (TRANSIENT_KEYS.has(k)) continue;
    out[k] = v;
  }
  return out;
};

/**
 * Normalizes a tree for comparison by removing transient data and sorting by node ID.
 * This ensures consistent comparison results regardless of node order or UI state.
 *
 * @param {Array} nodes - Array of tree nodes to normalize
 * @returns {Array} Normalized array of nodes suitable for comparison
 */
const normalizeTreeForCompare = (nodes) => {
  const arr = Array.isArray(nodes) ? nodes : [];
  return arr
    .map(stripTransient)
    .sort((a, b) =>
      String(a?.energyTreeNodeId ?? '').localeCompare(
        String(b?.energyTreeNodeId ?? ''),
      ),
    );
};

/**
 * Compares two tree structures for deep equality, ignoring transient UI properties and node order.
 *
 * @param {Array} a - First tree to compare
 * @param {Array} b - Second tree to compare
 * @returns {boolean} True if trees contain the same core data
 */
const jsonEqual = (a, b) =>
  JSON.stringify(normalizeTreeForCompare(a)) ===
  JSON.stringify(normalizeTreeForCompare(b));

/**
 * Creates a shallow clone of a tree node array.
 *
 * @param {Array} nodes - Tree nodes to clone
 * @returns {Array} New array with shallow cloned nodes
 */
const cloneTree = (nodes) =>
  Array.isArray(nodes) ? nodes.map((n) => ({ ...n })) : [];

/**
 * A React hook that manages tree data with draft functionality, allowing temporary modifications
 * before committing changes. Implements a three-state system: server tree (authoritative),
 * baseline tree (comparison reference), and draft tree (working copy).
 *
 * Key Features:
 * - Track changes with isDirty state
 * - Preserve or discard drafts when server data updates
 * - CRUD operations on tree nodes
 * - Commit/discard/reset functionality
 *
 * @param {Array} serverTree - The authoritative tree data from the server
 * @param {Object} options - Configuration options
 * @param {boolean} [options.preserveDraftOnServerChange=true] - Whether to keep draft changes when server updates
 * @param {string|null} [energyGroupId=null] - Default energy group ID for new nodes
 * @returns {Object} Tree draft management interface
 * @returns {Array} return.serverTree - The server tree (normalized)
 * @returns {Array} return.baselineTree - Reference tree for dirty comparison
 * @returns {Array} return.draftTree - Working tree that can be modified
 * @returns {Function} return.setDraftTree - Direct setter for draft tree (use with caution)
 * @returns {boolean} return.isDirty - Whether draft differs from baseline
 * @returns {Function} return.discardChanges - Reset draft to baseline
 * @returns {Function} return.resetToServer - Force both baseline and draft to server state
 * @returns {Function} return.commitDraft - Make draft the new baseline (call after successful save)
 * @returns {Function} return.addNode - Add a new node to the draft
 * @returns {Function} return.updateNode - Update an existing node in the draft
 * @returns {Function} return.moveNode - Move a node to a different parent
 * @returns {Function} return.deleteNode - Delete a node and optionally its descendants
 */
export function useTreeDraft(
  serverTree,
  { preserveDraftOnServerChange = true } = {},
  energyGroupId = null,
) {
  const server = useMemo(() => cloneTree(serverTree), [serverTree]);

  // Baseline tree: the reference point for determining if changes are "dirty"
  const [baselineTree, setBaselineTree] = useState(() => cloneTree(server));
  // Draft tree: the working copy that can be modified by the user
  const [draftTree, setDraftTree] = useState(() => cloneTree(server));

  const lastServerRef = useRef(server);

  const isDirty = useMemo(
    () => !jsonEqual(draftTree, baselineTree),
    [draftTree, baselineTree],
  );

  // Keep baseline/draft in sync with server when appropriate
  useEffect(() => {
    const prevServer = lastServerRef.current;
    lastServerRef.current = server;

    // If baseline was still aligned with previous server and we're not dirty,
    // treat incoming server as new baseline and update draft too.
    const baselineAlignedWithPrevServer = jsonEqual(baselineTree, prevServer);
    if (!isDirty && baselineAlignedWithPrevServer) {
      setBaselineTree(cloneTree(server));
      setDraftTree(cloneTree(server));
      return;
    }

    // If dirty and you do NOT want to preserve, overwrite both.
    if (isDirty && !preserveDraftOnServerChange) {
      setBaselineTree(cloneTree(server));
      setDraftTree(cloneTree(server));
      return;
    }

    // else: preserve draft/baseline (common in draft UX)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server, preserveDraftOnServerChange]);

  const discardChanges = useCallback(() => {
    setDraftTree(cloneTree(baselineTree));
  }, [baselineTree]);

  const resetToServer = useCallback(() => {
    // force baseline+draft to server (rarely needed)
    setBaselineTree(cloneTree(server));
    setDraftTree(cloneTree(server));
  }, [server]);

  // ✅ call this after successful save to make isDirty false immediately
  const commitDraft = useCallback(() => {
    setBaselineTree(cloneTree(draftTree));
    setDraftTree((prev) => cloneTree(prev)); // normalize immutability
  }, [draftTree]);

  /**
   * Adds a new node to the draft tree with proper defaults and ID generation.
   *
   * @param {Object} node - The node data to add
   * @param {string} [node.energyTreeNodeId] - Unique ID (generated if not provided)
   * @param {string} [node.energyGroupId] - Energy group ID (uses hook default if not provided)
   * @param {string} [node.parentId] - Parent node ID for hierarchy
   * @param {Array} [node.dataStreams] - Associated data streams
   * @param {Object} [node.options] - Node-specific options
   * @param {string} [node.locationId] - Associated location ID
   * @returns {string} The ID of the added node
   */
  const addNode = useCallback(
    (node) => {
      const id = node?.energyTreeNodeId ?? crypto.randomUUID();

      const nextNode = {
        // defaults
        energyGroupId: node?.energyGroupId ?? energyGroupId ?? null,

        // ensure these exist if your backend expects them
        parentId: node?.parentId ?? null,
        // Backend requires empty array, not null - Python code iterates without null-check
        dataStreams: Array.isArray(node?.dataStreams) ? node.dataStreams : [],
        options: node?.options ?? null,
        locationId: node?.locationId ?? null,

        // spread the rest last so caller can override defaults if needed
        ...node,

        // force ID stability
        energyTreeNodeId: id,
      };

      setDraftTree((prev) => [...prev, nextNode]);
      return id;
    },
    [energyGroupId],
  );

  /**
   * Updates an existing node in the draft tree by merging new properties.
   *
   * @param {string} nodeId - The ID of the node to update
   * @param {Object} patchOrNode - Properties to merge into the existing node
   */
  const updateNode = useCallback((nodeId, patchOrNode) => {
    setDraftTree((prev) =>
      prev.map((n) =>
        n.energyTreeNodeId === nodeId ? { ...n, ...patchOrNode } : n,
      ),
    );
  }, []);

  /**
   * Moves a node to a different parent in the tree hierarchy.
   *
   * @param {string} nodeId - The ID of the node to move
   * @param {string|null} newParentId - The ID of the new parent (null for root level)
   */
  const moveNode = useCallback((nodeId, newParentId) => {
    setDraftTree((prev) =>
      prev.map((n) =>
        n.energyTreeNodeId === nodeId ? { ...n, parentId: newParentId } : n,
      ),
    );
  }, []);

  /**
   * Deletes a node from the draft tree, optionally including all its descendants.
   * Uses depth-first traversal to find and remove all descendant nodes when includeDescendants is true.
   *
   * @param {string} nodeId - The ID of the node to delete
   * @param {Object} [options] - Deletion options
   * @param {boolean} [options.includeDescendants=true] - Whether to also delete all descendant nodes
   */
  const deleteNode = useCallback(
    (nodeId, { includeDescendants = true } = {}) => {
      setDraftTree((prev) => {
        if (!includeDescendants) {
          return prev.filter((n) => n.energyTreeNodeId !== nodeId);
        }

        // Build parent-to-children mapping for traversal
        const byParent = new Map();
        prev.forEach((n) => {
          const pid = n.parentId ?? '__root__';
          if (!byParent.has(pid)) byParent.set(pid, []);
          byParent.get(pid).push(n.energyTreeNodeId);
        });

        // Collect all nodes to delete using depth-first traversal
        const toDelete = new Set([nodeId]);
        const stack = [nodeId];
        while (stack.length) {
          const cur = stack.pop();
          const kids = byParent.get(cur) ?? [];
          for (const k of kids) {
            if (!toDelete.has(k)) {
              toDelete.add(k);
              stack.push(k);
            }
          }
        }

        return prev.filter((n) => !toDelete.has(n.energyTreeNodeId));
      });
    },
    [],
  );

  return {
    serverTree: server,
    baselineTree,
    draftTree,
    setDraftTree,
    isDirty,

    discardChanges,
    resetToServer,
    commitDraft,

    addNode,
    updateNode,
    moveNode,
    deleteNode,
  };
}
